import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzUploadModule, NzUploadFile } from "ng-zorro-antd/upload";
import { CoreDesiredOutcome, CoreDispute, CoreDisputeType, CoreHeldBackAmountType } from "@handshake/core-types";
import { CoreDisputeResolutionGuideComponent } from "../core-dispute-resolution-guide/core-dispute-resolution-guide.component";
import { getClientReleaseReasonLabel, getDisputeIssueTypeLabel, getHeldBackAmountLabel, getMilestoneInQuestionLabel, type CoreDesiredOutcomeOption } from "../core-raise-dispute-modal/core-raise-dispute-modal.config";

export interface CoreDisputeResolutionDocument {
  id: string;
  fileName: string;
  fileType?: string;
  createdAt: string;
  type: "evidence" | "resolution";
}

export interface CoreDisputeDetailConfig {
  dispute: CoreDispute & {
    projectMilestone?: { name?: string; project?: { name?: string; currency?: string } };
    resolutionDocuments?: CoreDisputeResolutionDocument[];
    /** Full name of the user who raised the dispute, resolved by the API from `createdBy` (HAN-636). */
    createdByName?: string | null;
  };
  project?: {
    name?: string;
    currency?: string;
    client?: { firstName?: string; lastName?: string };
    clientFirstName?: string;
    clientLastName?: string;
  };
  userRole: "client" | "business";
  /** When true, current user created this dispute. When disputeType is INITIAL_ATTEMPT, resolution docs are hidden from non-creators. */
  isCreator?: boolean;
  loading?: boolean;
  uploading?: boolean;
  partialReleaseOrRefund?: "partial_release" | "refund" | "none";
  /** Options used to resolve dispute.desiredOutcome enum to a display label on the dispute detail page. */
  desiredOutcomeOptions?: CoreDesiredOutcomeOption[];
  /** Interior Design company or its client: show the Aces Programme defect assessment step in the guide (HAN-633). */
  showDefectAssessment?: boolean;
}

@Component({
  selector: "app-core-dispute-detail",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzPopconfirmModule,
    NzToolTipModule,
    NzRadioModule,
    NzUploadModule,
    CoreDisputeResolutionGuideComponent,
  ],
  templateUrl: "./core-dispute-detail.component.html",
  styles: [
    `
      :host ::ng-deep .ant-upload {
        width: 100%;
      }
      :host ::ng-deep .ant-upload-drag {
        background: #fafafa;
        border: 1px dashed #d9d9d9;
        border-radius: 8px;
      }
      :host ::ng-deep .ant-upload-drag:hover {
        border-color: #1890ff;
      }
    `,
  ],
})
export class CoreDisputeDetailComponent {
  @Input({ required: true }) config!: CoreDisputeDetailConfig;
  @Output() agreeToMutualResolution = new EventEmitter<{ disputeId: string; isAccepting: boolean }>();
  @Output() submitSettlementAgreement = new EventEmitter<string>();
  /** Emitted when user accepts the other party's settlement (not when submitting their own). */
  @Output() acceptSettlementAgreement = new EventEmitter<{ disputeId: string; resolutionType: string }>();
  /** Emitted when user rejects the other party's mutual resolution or settlement agreement; reason is required. */
  @Output() rejectResolution = new EventEmitter<{ disputeId: string; reason: string }>();
  @Output() cancelSubmission = new EventEmitter<string>();
  @Output() uploadFile = new EventEmitter<{ disputeId: string; file: File }>();
  @Output() deleteFile = new EventEmitter<{ disputeId: string; documentId: string }>();
  @Output() downloadFile = new EventEmitter<{ disputeId: string; document: CoreDisputeResolutionDocument }>();
  @Output() openWhatsApp = new EventEmitter<void>();
  @Output() contactSupport = new EventEmitter<void>();
  @Output() partialReleaseOrRefundChange = new EventEmitter<{ disputeId: string; value: "partial_release" | "refund" | "none" }>();

  /** Per-dispute selection so multiple disputes on the page don't share state. */
  private readonly partialReleaseOrRefundByDisputeId = new Map<string, "partial_release" | "refund" | "none">();

  /** When set, show the reject-reason form for this dispute (mutual or settlement). */
  rejectFormDisputeId: string | null = null;
  rejectReason = "";

  readonly DisputeType = CoreDisputeType;

  // Helper method to parse issues into a list
  parseIssues(describeIssue: string | undefined): string[] {
    if (!describeIssue) return [];
    return describeIssue
      .split(/\n|•|-\s*/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  // Helper method to parse expectations into a list.
  parseExpectations(expectation: string | undefined): string[] {
    if (!expectation) return [];
    return expectation
      .split(/\n|•|-\s*/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  /** Heading for dispute.expectation: "Client to release funds" disputes hold the amount held back and the client's reason. */
  getExpectationHeading(desiredOutcome: string | undefined): string {
    if (desiredOutcome !== CoreDesiredOutcome.CLIENT_RELEASE_FUNDS) return "Expectation";
    // HAN-692: the amount has its own section, so the text is only the reason
    return this.config.dispute?.heldBackAmount != null ? "Reason" : "Amount held and reason";
  }

  /** HAN-692: "Amount held back" for both parties; null when none was recorded. */
  getHeldBackAmountLabel(): string | null {
    const dispute = this.config.dispute;
    return getHeldBackAmountLabel(dispute, this.config.project?.currency ?? dispute?.projectMilestone?.project?.currency);
  }

  /**
   * Hide the reason section for "Full balance" disputes: no reason is asked, and the stored text is only the
   * issue repeated (the API falls back to it).
   */
  showExpectation(): boolean {
    return this.config.dispute?.heldBackAmountType !== CoreHeldBackAmountType.FULL_BALANCE;
  }

  /** Resolve dispute.desiredOutcome (enum value) to display label when config.desiredOutcomeOptions is provided. */
  getDesiredOutcomeLabel(desiredOutcome: string | undefined): string | null {
    if (!desiredOutcome) return null;
    const options = this.config.desiredOutcomeOptions;
    if (!options?.length) return null;
    const option = options.find((opt) => opt.value === desiredOutcome);
    return option?.label ?? null;
  }

  /** "Milestone in question" value shown to both parties (HAN-637), or null for disputes raised before it was asked. */
  getMilestoneInQuestionLabel(): string | null {
    return getMilestoneInQuestionLabel(this.config.dispute);
  }

  /** Label for the type of issue (disputes raised by clients of ID companies, HAN-638), or null when not recorded. */
  getDisputeIssueTypeLabel(value: string | null | undefined): string | null {
    return getDisputeIssueTypeLabel(value);
  }

  /** Label for the client's reason (ID "Client to release funds" disputes), or null when not recorded. */
  getClientReleaseReasonLabel(value: string | null | undefined): string | null {
    return getClientReleaseReasonLabel(value);
  }

  // Get resolution documents (type = resolution)
  getResolutionDocuments(documents: CoreDisputeResolutionDocument[] | undefined): CoreDisputeResolutionDocument[] {
    if (!documents) return [];
    return documents.filter((doc) => doc.type === "resolution");
  }

  // Get evidence documents (type = evidence)
  getEvidenceDocuments(documents: CoreDisputeResolutionDocument[] | undefined): CoreDisputeResolutionDocument[] {
    if (!documents) return [];
    return documents.filter((doc) => doc.type === "evidence");
  }

  getResolutionTypeLabel(disputeType: CoreDisputeType | undefined): string {
    if (!disputeType) return "N/A";
    switch (disputeType) {
      case CoreDisputeType.FORMAL_RESOLUTION:
        return "Formal Resolution";
      case CoreDisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE:
        return "Formal Resolution with Partial Release";
      case CoreDisputeType.FORMAL_RESOLUTION_WITH_REFUND:
        return "Formal Resolution with Refund";
      case CoreDisputeType.MUTUAL_RESOLUTION:
        return "Mutual Resolution";
      default:
        return disputeType || "N/A";
    }
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    const actualFile = file.originFileObj || (file as any);
    if (!actualFile || !(actualFile instanceof File)) {
      return false;
    }

    // Only allow PDF files
    if (actualFile.type !== "application/pdf") {
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (actualFile.size > maxSize) {
      return false;
    }

    if (this.config.dispute.id) {
      this.uploadFile.emit({ disputeId: this.config.dispute.id, file: actualFile });
    }

    return false; // Prevent auto upload
  };

  onPartialReleaseOrRefundChange(value: "partial_release" | "refund" | "none"): void {
    const disputeId = this.config.dispute.id;
    if (disputeId) {
      this.partialReleaseOrRefundByDisputeId.set(disputeId, value);
      this.partialReleaseOrRefundChange.emit({ disputeId, value });
    }
  }

  /** Current partial release/refund value for this dispute (isolated per dispute when multiple are shown). */
  getPartialReleaseOrRefundValue(): "partial_release" | "refund" | "none" {
    if (this.otherPartySelectedFormalResolution() || this.isFormalResolutionChoiceLocked()) {
      return this.getPartialReleaseOrRefundFromDisputeType();
    }
    const disputeId = this.config.dispute.id;
    if (disputeId && this.partialReleaseOrRefundByDisputeId.has(disputeId)) {
      return this.partialReleaseOrRefundByDisputeId.get(disputeId)!;
    }
    return this.config.partialReleaseOrRefund ?? "none";
  }

  /**
   * A dispute locks all funds on the project, so it is titled after the project,
   * never the milestone in progress (HAN-632).
   */
  getDisputeTitle(): string {
    const projectName = (this.config.project?.name || this.config.dispute.projectMilestone?.project?.name || "").trim();
    return projectName ? `Dispute: ${projectName}` : "Dispute";
  }

  /**
   * Who raised the dispute, shown to both parties as "Issued by <name>" (HAN-636).
   * Uses the API-resolved creator name; for a business viewing a client-raised dispute it can
   * fall back to the project's client name. Returns "" when unknown, so only the date is shown.
   */
  getIssuerName(): string {
    const createdByName = this.config.dispute.createdByName?.trim();
    if (createdByName) return createdByName;
    if (this.isBusiness() && this.config.isCreator === false) {
      const project = this.config.project;
      const firstName = project?.client?.firstName || project?.clientFirstName || "";
      const lastName = project?.client?.lastName || project?.clientLastName || "";
      return `${firstName} ${lastName}`.trim();
    }
    return "";
  }

  isClient(): boolean {
    return this.config.userRole === "client";
  }

  isBusiness(): boolean {
    return this.config.userRole === "business";
  }

  getSubmissionStatusMessage(): string {
    const disputeType = this.config.dispute.disputeType;
    const isMutualResolution = disputeType === CoreDisputeType.MUTUAL_RESOLUTION;

    if (this.isFormalResolutionType()) {
      return (
        "Your settlement agreement has been sent.\n" +
        "The other party has been notified to review it. You will be notified once they accept or reject the agreement."
      );
    }

    const resolutionType = "mutual";
    if (this.isClient()) {
      return this.config.dispute.clientAgreedToResolve
        ? `You have successfully submitted your ${resolutionType} resolution request. Waiting for business to review.`
        : "";
    } else {
      return this.config.dispute.businessAgreedToResolve
        ? `You have successfully submitted your ${resolutionType} resolution request. Waiting for client to review.`
        : "";
    }
  }

  hasSubmitted(): boolean {
    if (this.isClient()) {
      return !!this.config.dispute.clientAgreedToResolve;
    } else {
      return !!this.config.dispute.businessAgreedToResolve;
    }
  }

  otherPartyHasSubmitted(): boolean {
    if (this.isClient()) {
      return !!this.config.dispute.businessAgreedToResolve;
    } else {
      return !!this.config.dispute.clientAgreedToResolve;
    }
  }

  // Check if other party selected mutual resolution
  otherPartySelectedMutualResolution(): boolean {
    return (
      this.config.dispute.disputeType === CoreDisputeType.MUTUAL_RESOLUTION &&
      this.otherPartyHasSubmitted()
    );
  }

  // Check if other party selected formal resolution
  otherPartySelectedFormalResolution(): boolean {
    return (
      (this.config.dispute.disputeType === CoreDisputeType.FORMAL_RESOLUTION ||
        this.config.dispute.disputeType === CoreDisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE ||
        this.config.dispute.disputeType === CoreDisputeType.FORMAL_RESOLUTION_WITH_REFUND) &&
      this.otherPartyHasSubmitted()
    );
  }

  // Get partial release or refund value from dispute type
  getPartialReleaseOrRefundFromDisputeType(): "partial_release" | "refund" | "none" {
    const dispute = this.config.dispute;
    if (dispute.disputeType === CoreDisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE) {
      return "partial_release";
    } else if (dispute.disputeType === CoreDisputeType.FORMAL_RESOLUTION_WITH_REFUND) {
      return "refund";
    } else {
      return "none";
    }
  }

  /** True when dispute type is one of the formal resolution types (not mutual or initial). */
  isFormalResolutionType(): boolean {
    const t = this.config.dispute.disputeType;
    return (
      t === CoreDisputeType.FORMAL_RESOLUTION ||
      t === CoreDisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE ||
      t === CoreDisputeType.FORMAL_RESOLUTION_WITH_REFUND
    );
  }

  /** When INITIAL_ATTEMPT, only creator sees the list of uploaded resolution docs; upload form is shown to both. */
  showResolutionDocumentsSection(): boolean {
    const dt = this.config.dispute.disputeType;
    const isInitialAttempt =
      dt === CoreDisputeType.INITIAL_ATTEMPT || (typeof dt === "string" && dt?.toLowerCase() === "initial_attempt");
    if (!isInitialAttempt) return true;
    return this.config.isCreator === true;
  }

  /** True when current user has submitted and choice is locked (formal resolution). */
  isFormalResolutionChoiceLocked(): boolean {
    return this.hasSubmitted() && this.isFormalResolutionType();
  }

  openRejectForm(disputeId: string): void {
    this.rejectFormDisputeId = disputeId;
    this.rejectReason = "";
  }

  cancelRejectForm(): void {
    this.rejectFormDisputeId = null;
    this.rejectReason = "";
  }

  /** Emit accept-settlement payload so the parent can pass the correct resolution type (required for partial-release/refund). */
  emitAcceptSettlementAgreement(): void {
    const dispute = this.config.dispute;
    this.acceptSettlementAgreement.emit({
      disputeId: dispute.id,
      resolutionType: dispute.disputeType || CoreDisputeType.FORMAL_RESOLUTION,
    });
  }

  submitReject(): void {
    if (!this.rejectFormDisputeId || !this.rejectReason?.trim()) return;
    this.rejectResolution.emit({ disputeId: this.rejectFormDisputeId, reason: this.rejectReason.trim() });
    this.cancelRejectForm();
  }
}
